import { useNavigation } from '../app/navigation';
import type { RouteId } from '../app/routes';
import { Button } from '../components/ui/Button';
import { Icon, type IconName } from '../components/ui/Icon';
import { Panel } from '../components/ui/Panel';
import { useGame } from '../game/store';
import { useSession } from '../state/session';
import { creditIconFor } from '../components/ui/currency';
import { creditUnit, creditWord, formatCreditAmount, formatCredits } from '../lib/format';
import layout from '../styles/layout.module.css';
import styles from './HowToPlayPage.module.css';

interface Step {
  icon: IconName;
  title: string;
  body: string;
  cta?: { label: string; route: RouteId };
}

/**
 * The rules, in the order they happen.
 *
 * Written from what the player does rather than from what the app contains, and
 * every number on it is read from the live config — a page that explains a
 * 10-credit spin while the owner charges 25 is worse than no page.
 */
export function HowToPlayPage() {
  const { navigate } = useNavigation();
  const { wheel, naming } = useGame();
  const { theme } = useSession();

  const lucky = wheel('main');
  const premium = wheel('daily');
  const money = creditWord(naming);

  const steps: Step[] = [
    {
      icon: creditIconFor(theme),
      title: `Get ${money}`,
      body: `${money} are a thank-you for supporting the creator where they post — they mirror what you have actually done, so there is nothing to claim for free. Spoyl never charges you and sells nothing: ${money} are not something you buy here.`,
      cta: { label: 'See how to earn', route: 'shop' },
    },
    {
      icon: 'wheel',
      title: 'Spin a drop',
      body: `${lucky.name} costs ${formatCreditAmount(naming, lucky.spinCost)} a spin. ${
        premium.name
      } costs ${formatCredits(premium.spinCost)} and carries the bigger prizes. Every slice shows its odds before you spin.`,
      cta: { label: 'Play the Lucky Drop', route: 'wheel' },
    },
    {
      icon: 'gift',
      title: 'Win rewards',
      body: `${creditUnit(naming, 2)} and coupons land in your account straight away. Anything else — a role, a custom reward — goes to My Rewards and waits there until it has been handed over.`,
      cta: { label: 'See my rewards', route: 'rewards' },
    },
    {
      icon: 'users',
      title: 'The team takes it from there',
      body: `You never have to chase anyone. Claiming ${money} tells the team immediately, and every prize you win sits in their queue until it has been handed over. They come back to you to confirm, deliver a reward, or honour a coupon.`,
    },
  ];

  return (
    <>
      <section className={styles.intro}>
        <h2 className={styles.introTitle}>How Spoyl works</h2>
        <p className={styles.introText}>
          Four steps, and the only one that leaves the app is the last.
        </p>
      </section>

      <div className={layout.gridWide}>
        {steps.map((step, index) => (
          <article key={step.title} className={styles.step}>
            <span className={styles.badge}>
              <Icon name={step.icon} size={18} />
            </span>
            <span className={styles.index}>Step {index + 1}</span>
            <h3 className={styles.stepTitle}>{step.title}</h3>
            <p className={styles.stepBody}>{step.body}</p>
            {step.cta && (
              <Button
                size="sm"
                variant="secondary"
                iconAfter="chevronRight"
                onClick={() => navigate(step.cta!.route)}
              >
                {step.cta.label}
              </Button>
            )}
          </article>
        ))}
      </div>

      <Panel title="Coupons" subtitle="Stackable, single use">
        <p className={styles.note}>
          A coupon takes a percentage off your next purchase on the creator's page. You can use one
          or all of them at once, and each code works exactly once. Your codes are on the Earn{' '}
          {money} page — quote one there, not here.
        </p>
      </Panel>

      <Panel title="Fair play" subtitle="What the odds mean">
        <p className={styles.note}>
          Every slice shows the chance the owner advertises for it, and the draw runs on the server
          — never in your browser. A result cannot be changed once the wheel starts turning.
        </p>
      </Panel>
    </>
  );
}
